import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { MobileDateFilter } from './MobileDateFilter';
import { MobileFilterMenuItem } from './MobileFilterMenuItem';
import { hasActiveFilters } from '../../../../../play-core/api/media-search/search-media';
import { getAggregationItemsTitle } from '../../../../../play-core/models/search/MediaSearchResponse';
import { useAnalyticsDispatch } from '../../../analytics/AnalyticsContext';
import { getDateFilterName } from '../../../components/MediaSearch/date-filter-helper';
import { MobileDialog } from '../../../components/MobileDialog/MobileDialog';
import { MobileDialogHeader } from '../../../components/MobileDialog/MobileDialogHeader';
import { MobileGroupedSelection } from '../../../components/MobileSelection/MobileGroupedSelection';
import { MobileSelection } from '../../../components/MobileSelection/MobileSelection';
import { useSearchDispatch } from '../SearchContext';
import { getActivePropertyFilters, toDropdownItemGroup } from '../SearchFilters/search-filter-helpers';
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
export const MobileSearchFilters = ({
  isOpen,
  isLoading,
  query,
  mediaResult,
  dates = []
}) => {
  const dispatch = useSearchDispatch();
  const analytics = useAnalyticsDispatch();
  const searchFilterMetas = {
    searchTerm: query.searchTerm
  };
  const {
    t
  } = useTranslation();
  const [topicsOpen, setTopicsOpen] = useState(false);
  const [showsOpen, setShowsOpen] = useState(false);
  const [dateRangeOpen, setDateRangeOpen] = useState(false);
  const [propertiesOpen, setPropertiesOpen] = useState(false);
  const dateFilterActive = !!(query.from && query.to);
  const searchTerm = query.searchTerm ? `‘${query.searchTerm}‘` : '';
  return /*#__PURE__*/_jsxs(MobileDialog, {
    dialogId: "mobile-filters",
    isOpen: isOpen,
    title: `${t('search.filter')} ${searchTerm}`,
    onClose: () => dispatch({
      type: 'set-mobile-filters-open',
      isOpen: false
    }),
    children: [/*#__PURE__*/_jsx(MobileDialogHeader, {
      closeIcon: "close",
      title: `${t('search.filter')} ${searchTerm}`,
      isLoading: isLoading,
      actionText: t('global.reset'),
      actionVisible: hasActiveFilters(query),
      onAction: () => dispatch({
        type: 'remove-all-filters'
      })
    }), /*#__PURE__*/_jsx(MobileFilterMenuItem, {
      title: t('global.topics'),
      text: getAggregationItemsTitle(query.topics ?? [], 'topics', mediaResult),
      onClick: () => setTopicsOpen(true),
      isEnabled: !isLoading
    }), /*#__PURE__*/_jsx(MobileDialog, {
      dialogId: "topic-filter",
      isOpen: topicsOpen,
      title: t('global.topics'),
      onClose: () => setTopicsOpen(false),
      children: /*#__PURE__*/_jsx(MobileSelection, {
        title: t('global.topics'),
        saveButtonText: t('search.showResults'),
        items: mediaResult && mediaResult.aggregations && mediaResult.aggregations.topics || [],
        isLoading: isLoading,
        selectedItemIds: query.topics ?? [],
        onChange: topics => {
          dispatch({
            type: 'set-topic-filter',
            topics
          });
          analytics({
            type: 'change-search-topic-filter',
            ...searchFilterMetas
          });
        }
      })
    }), /*#__PURE__*/_jsx(MobileFilterMenuItem, {
      title: t('global.shows'),
      text: getAggregationItemsTitle(query.shows ?? [], 'shows', mediaResult),
      onClick: () => setShowsOpen(true),
      isEnabled: !isLoading
    }), /*#__PURE__*/_jsx(MobileDialog, {
      dialogId: "show-filter",
      isOpen: showsOpen,
      title: t('global.shows'),
      onClose: () => setShowsOpen(false),
      children: /*#__PURE__*/_jsx(MobileSelection, {
        title: t('global.shows'),
        saveButtonText: t('search.showResults'),
        items: mediaResult && mediaResult.aggregations && mediaResult.aggregations.shows || [],
        isLoading: isLoading,
        selectedItemIds: query.shows ?? [],
        onChange: shows => {
          dispatch({
            type: 'set-show-filter',
            shows
          });
          analytics({
            type: 'change-search-show-filter',
            ...searchFilterMetas
          });
        }
      })
    }), /*#__PURE__*/_jsx(MobileFilterMenuItem, {
      title: t('search.dateRangeFilter'),
      text: dateFilterActive ? t(getDateFilterName(query.from, query.to)) : undefined,
      onClick: () => setDateRangeOpen(true),
      isEnabled: !isLoading
    }), /*#__PURE__*/_jsxs(MobileDialog, {
      dialogId: "date-filter",
      isOpen: dateRangeOpen,
      title: t('search.dateRangeFilter'),
      onClose: () => setDateRangeOpen(false),
      children: [/*#__PURE__*/_jsx(MobileDialogHeader, {
        closeIcon: "back",
        title: t('search.dateRangeFilter'),
        isLoading: isLoading,
        actionText: t('global.reset'),
        actionVisible: dateFilterActive,
        onAction: () => dispatch({
          type: 'remove-date-filter'
        })
      }), /*#__PURE__*/_jsx(MobileDateFilter, {
        dates: dates,
        selection: {
          from: query.from,
          to: query.to
        },
        onChange: dateRange => {
          dispatch({
            type: 'set-date-filter',
            dateRange
          });
          analytics({
            type: 'change-search-date-filter',
            ...searchFilterMetas
          });
        }
      })]
    }), /*#__PURE__*/_jsx(MobileFilterMenuItem, {
      title: t('search.propertyFilter'),
      text: getPropertyFilters(t, query),
      isEnabled: !isLoading,
      onClick: () => setPropertiesOpen(true)
    }), /*#__PURE__*/_jsx(MobileDialog, {
      dialogId: "property-filter",
      isOpen: propertiesOpen,
      title: t('search.propertyFilter'),
      onClose: () => setPropertiesOpen(false),
      children: /*#__PURE__*/_jsx(MobileGroupedSelection, {
        title: t('search.propertyFilter'),
        saveButtonText: t('search.showResults'),
        isLoading: isLoading,
        groups: toDropdownItemGroup(mediaResult, query, t),
        onChange: newFilters => {
          const filters = getActivePropertyFilters(newFilters);
          dispatch({
            type: 'set-media-property-filters',
            filters
          });
          Object.values(filters).flat().forEach(property => {
            analytics({
              type: 'change-search-property-filter',
              property,
              ...searchFilterMetas
            });
          });
        }
      })
    })]
  });
};
const getPropertyFilters = (t, query) => {
  let labels = [];
  if (query.mediaProperties) {
    labels = [...labels, ...(query.mediaProperties?.map(id => t(`search.property.${id}`)) ?? [])];
  }
  if (query.mediaType) {
    labels.push(t(`search.mediaType.${query.mediaType}`));
  }
  return labels.join(', ');
};